/**
 * Documentation API — response contracts.
 */

import { z } from 'zod'

import { DocBaseSchema, DocNavigationItemSchema, DocReaderPageSchema, DocSchema } from '../../schemas/entities/doc'

// ============================================
// Reader navigation
// ============================================

/**
 * Every published page the VIEWER may open, flat. The client groups by
 * `section` and orders by `sectionOrder` — the server returns it pre-sorted in
 * exactly that order so a client that renders the array as-is is already
 * correct.
 *
 * Link-shareable pages are absent for every viewer, including a super-admin:
 * their whole point is that the URL is the only way in.
 */
export const GetDocsNavigationResponseSchema = z.object({
  data: z.array(DocNavigationItemSchema)
})
export type GetDocsNavigationResponse = z.infer<typeof GetDocsNavigationResponseSchema>

// ============================================
// Reader page (by slug, and by share token)
// ============================================

/**
 * The page itself. Both reader routes answer with the SAME shape — one resolved
 * by slug, one by share token — so the route that renders it (U11) has one
 * template rather than two that can drift on the security-relevant field.
 *
 * A page the requester may not open is NOT represented here. It comes back as
 * an error whose `type` carries the decision: `DOC_NOT_FOUND` (the page does not
 * exist, is unpublished, or is one whose existence must not be revealed) or
 * `DOC_AUTH_REQUIRED` (it exists and a session would open it — redirect to
 * login). Those two are the four-state model's whole observable surface, and
 * they must not be collapsed into one code.
 */
export const GetDocPageResponseSchema = DocReaderPageSchema
export type GetDocPageResponse = z.infer<typeof GetDocPageResponseSchema>

// ============================================
// Admin reads
// ============================================

export const DocListItemSchema = DocBaseSchema.extend({
  hasDraft: z.boolean(),
  isPublished: z.boolean(),
  /** True once the page has ever been published — the slug is read-only from then on. */
  slugLocked: z.boolean(),
  publishedAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime()
})
export type DocListItem = z.infer<typeof DocListItemSchema>

export const ListDocsResponseSchema = z.object({
  data: z.array(DocListItemSchema)
})
export type ListDocsResponse = z.infer<typeof ListDocsResponseSchema>

export const GetDocResponseSchema = DocSchema
export type GetDocResponse = z.infer<typeof GetDocResponseSchema>

// ============================================
// Admin writes
// ============================================

export const CreateDocResponseSchema = z.object({
  publicId: z.string(),
  slug: z.string()
})
export type CreateDocResponse = z.infer<typeof CreateDocResponseSchema>

export const UpdateDocDraftResponseSchema = z.object({
  publicId: z.string(),
  slug: z.string(),
  /** Null when the update touched only metadata and the page has no DRAFT version. */
  draftPublicId: z.string().nullable()
})
export type UpdateDocDraftResponse = z.infer<typeof UpdateDocDraftResponseSchema>

export const PublishDocResponseSchema = z.object({
  publicId: z.string(),
  publishedVersionPublicId: z.string(),
  publishedAt: z.string().datetime()
})
export type PublishDocResponse = z.infer<typeof PublishDocResponseSchema>

export const UnpublishDocResponseSchema = z.object({
  publicId: z.string(),
  /** The version the published one became — unpublish is publish's inverse, not a delete. */
  draftPublicId: z.string()
})
export type UnpublishDocResponse = z.infer<typeof UnpublishDocResponseSchema>

/**
 * The NEW token, returned once. The previous one is already dead by the time
 * this response is written — rotation IS revocation — and the page is still
 * published, which is the point: an author revokes a leaked link without taking
 * the page down.
 */
export const RotateDocShareTokenResponseSchema = z.object({
  publicId: z.string(),
  shareToken: z.string()
})
export type RotateDocShareTokenResponse = z.infer<typeof RotateDocShareTokenResponseSchema>
